/**
 * My Follows Dashboard Page
 *
 * 내 팔로우 대시보드 페이지
 * - 팔로우 중인 포트폴리오 목록
 * - 실시간 포지션 모니터링 (WebSocket)
 * - 총 손익 및 통계
 * - 팔로우 관리 (일시중지, 재개, 종료)
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { Button, Table, Tag, message, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIsTablet } from '@utils/CalVW.tsx';
import { formatDecimal } from '@utils/formatDecimal';
import { getBigNumber } from '@utils/getBigNumber';

import { useCopyPortfolioStore } from '../../hooks/copyTrading/copyPortfolioStore';
import { useCopyWebsocketStore } from '../../hooks/copyTrading/copyWebsocketStore';
import { useTradingWallets } from '../../hooks/user/useTradingWallets';
import { EditFollowModal } from './modal/EditFollowModal';

import type {
  CopyFollow,
  CopyPortfolio,
  CopyPosition,
} from '../../../types/copyTrading';

export function MyFollowsPage() {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedFollow, setSelectedFollow] = useState<CopyFollow | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<CopyPortfolio | null>(null);

  // Zustand Stores
  const {
    myFollows,
    fetchMyFollows,
    pauseFollow,
    resumeFollow,
    stopFollow,
    isLoading,
    summary,
  } = useCopyPortfolioStore();

  const { positions, alerts, isConnected, connect, disconnect, removeAlert } =
    useCopyWebsocketStore();

  // 카피 지갑 정보
  const { data: walletData, getCopyAvailable } = useTradingWallets();

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    // Fetch user's follows
    fetchMyFollows();

    // Connect to WebSocket for real-time position updates
    connect();

    return () => {
      // Disconnect WebSocket on unmount
      disconnect();
    };
  }, []);

  // 디버깅: myFollows 변경 감지
  useEffect(() => {
    console.log('[MyFollowsPage] Current myFollows:', myFollows);
    console.log('[MyFollowsPage] myFollows length:', (myFollows || []).length);
  }, [myFollows]);

  // ==========================================
  // Handlers (메모이제이션)
  // ==========================================

  const handlePause = useCallback(
    async (followId: number) => {
      try {
        await pauseFollow(followId);
        message.success(t('followPaused'));
      } catch (error: any) {
        message.error(error.message || t('pauseFailed'));
      }
    },
    [pauseFollow, t]
  );

  const handleResume = useCallback(
    async (followId: number) => {
      try {
        await resumeFollow(followId);
        message.success(t('followResumed'));
      } catch (error: any) {
        message.error(error.message || t('resumeFailed'));
      }
    },
    [resumeFollow, t]
  );

  const handleEdit = useCallback(
    (follow: CopyFollow, portfolio: CopyPortfolio) => {
      setSelectedFollow(follow);
      setSelectedPortfolio(portfolio);
      setOpenEditModal(true);
    },
    []
  );

  const handleStop = useCallback(
    (followId: number) => {
      Modal.confirm({
        title: t('stopFollow'),
        content: t('stopFollowConfirm'),
        okText: t('stop'),
        cancelText: t('cancel'),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await stopFollow(followId);
            message.success(t('followStopped'));
          } catch (error: any) {
            message.error(error.message || t('stopFailed'));
          }
        },
      });
    },
    [stopFollow, t]
  );

  // ==========================================
  // Table Columns (메모이제이션)
  // ==========================================

  const followColumns: ColumnsType<any> = useMemo(
    () => [
      {
        title: t('portfolio'),
        dataIndex: 'portfolio',
        key: 'portfolio',
        render: (portfolio: CopyPortfolio) => {
          if (!portfolio) return '-';
          return (
            <div>
              <div style={{ fontWeight: 600 }}>
                {portfolio.portfolioName || '-'}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                by {portfolio.leaderUsername || '-'}
              </div>
            </div>
          );
        },
      },
      {
        title: t('status'),
        dataIndex: 'follow',
        key: 'status',
        render: (follow: CopyFollow) => {
          if (!follow) return '-';
          const statusMap = {
            1: { text: t('active'), color: 'green' },
            2: { text: t('paused'), color: 'orange' },
            3: { text: t('stopped'), color: 'red' },
          };
          const status = statusMap[follow.status as 1 | 2 | 3] || statusMap[1];
          return <Tag color={status.color}>{status.text}</Tag>;
        },
      },
      {
        title: t('copyRatio'),
        dataIndex: 'follow',
        key: 'copyRatio',
        render: (follow: CopyFollow) => {
          if (!follow || !follow.copyRatio) return '-';
          return `${formatDecimal(parseFloat(String(follow.copyRatio)) * 100, 0)}%`;
        },
      },
      {
        title: t('allocatedAmount'),
        dataIndex: 'follow',
        key: 'allocatedAmount',
        render: (follow: CopyFollow) => {
          if (!follow || !follow.allocatedAmount) return '-';
          return `${formatDecimal(parseFloat(String(follow.allocatedAmount)), 2)} USDT`;
        },
      },
      {
        title: t('currentPnl'),
        dataIndex: 'follow',
        key: 'currentPnl',
        render: (follow: CopyFollow) => {
          if (!follow) return '-';
          const pnl = parseFloat(String(follow.currentPnl || '0'));
          const isPositive = pnl >= 0;
          return (
            <span
              style={{
                color: isPositive ? '#22c55e' : '#ef4444',
                fontWeight: 600,
              }}
            >
              {isPositive ? '+' : ''}
              {formatDecimal(pnl, 2)} USDT
            </span>
          );
        },
      },
      {
        title: t('roi'),
        dataIndex: 'follow',
        key: 'roi',
        render: (follow: CopyFollow) => {
          const roi = getBigNumber(follow.currentPnl)
            .dividedBy(follow.allocatedAmount)
            .multipliedBy(100)
            .toNumber();
          const isPositive = roi >= 0;
          return (
            <span
              style={{
                color: isPositive ? '#22c55e' : '#ef4444',
                fontWeight: 600,
              }}
            >
              {isPositive ? '+' : ''}
              {formatDecimal(roi, 2)}%
            </span>
          );
        },
      },
      {
        title: t('manage'),
        key: 'actions',
        render: (_, record) => {
          const follow = record.follow as CopyFollow;
          const portfolio = record.portfolio as CopyPortfolio;
          const isActive = follow.status === 1;
          const isPaused = follow.status === 2;

          if (follow.status === 3) return; // 완전히 삭제하는 버튼이 들어가야함.
          return (
            <ActionsGroup>
              <Button
                size="small"
                onClick={() => handleEdit(follow, portfolio)}
              >
                {t('settings')}
              </Button>
              {isActive && (
                <Button size="small" onClick={() => handlePause(follow.id)}>
                  {t('pause')}
                </Button>
              )}
              {isPaused && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => handleResume(follow.id)}
                >
                  {t('resume')}
                </Button>
              )}
              <Button size="small" danger onClick={() => handleStop(follow.id)}>
                {t('stop')}
              </Button>
            </ActionsGroup>
          );
        },
      },
    ],
    [handleEdit, handlePause, handleResume, handleStop, t]
  );

  const positionColumns: ColumnsType<CopyPosition> = useMemo(
    () => [
      {
        title: t('symbol'),
        dataIndex: 'symbol',
        key: 'symbol',
        render: (symbol) => <strong>{symbol}</strong>,
      },
      {
        title: t('direction'),
        dataIndex: 'side',
        key: 'side',
        render: (side: 'LONG' | 'SHORT') => (
          <Tag color={side === 'LONG' ? 'green' : 'red'}>{side}</Tag>
        ),
      },
      {
        title: t('entryPrice'),
        dataIndex: 'entryPrice',
        key: 'entryPrice',
        render: (price) => formatDecimal(price, 2),
      },
      {
        title: t('currentPrice'),
        dataIndex: 'currentPrice',
        key: 'currentPrice',
        render: (price) => formatDecimal(price, 2),
      },
      {
        title: t('quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (quantity) => formatDecimal(quantity, 4),
      },
      {
        title: t('leverage'),
        dataIndex: 'leverage',
        key: 'leverage',
        render: (leverage) => `${leverage}x`,
      },
      {
        title: t('unrealizedPnl'),
        dataIndex: 'unrealizedPnl',
        key: 'unrealizedPnl',
        render: (pnl) => {
          const isPositive = pnl >= 0;
          return (
            <span
              style={{
                color: isPositive ? '#22c55e' : '#ef4444',
                fontWeight: 600,
              }}
            >
              {isPositive ? '+' : ''}
              {formatDecimal(pnl, 2)} USDT
            </span>
          );
        },
      },
    ],
    [t]
  );

  // ==========================================
  // Render
  // ==========================================

  return (
    <>
      {/* Edit Follow Modal */}
      <EditFollowModal
        open={openEditModal}
        setOpen={setOpenEditModal}
        follow={selectedFollow}
        portfolio={selectedPortfolio}
      />

      <PageWrapper>
        <CompactHeader isDark={isDarkMode}>
          <HeaderLeft>
            <HeaderTitle>
              {t('myFollowsDashboard')}
              <ConnectionIndicator isConnected={isConnected} />
            </HeaderTitle>
          </HeaderLeft>

          {/* Inline Summary Stats */}
          <StatsRow>
            <StatItem>
              <StatLabel>{t('copyWalletBalance')}</StatLabel>
              <StatValue>
                {formatDecimal(parseFloat(getCopyAvailable()), 2)} USDT
              </StatValue>
              {walletData?.copy && (
                <StatSubtext>
                  {t('locked')}:{' '}
                  {formatDecimal(parseFloat(walletData.copy.lockbalance), 2)}{' '}
                  USDT
                </StatSubtext>
              )}
            </StatItem>
            <StatDivider />
            <StatItem>
              <StatLabel>{t('totalAllocated')}</StatLabel>
              <StatValue>
                {formatDecimal(
                  walletData?.copy
                    ? parseFloat(walletData.copy.lockbalance)
                    : 0,
                  2
                )}{' '}
                USDT
              </StatValue>
            </StatItem>
            <StatDivider />
            <StatItem>
              <StatLabel>{t('totalPnl')}</StatLabel>
              <StatValue
                style={{
                  color: (summary?.totalPnl ?? 0) >= 0 ? '#22c55e' : '#ef4444',
                }}
              >
                {(summary?.totalPnl ?? 0) >= 0 ? '+' : ''}
                {formatDecimal(
                  summary?.totalPnl ?? 0,
                  2
                )} USDT
              </StatValue>
            </StatItem>
            <StatDivider />
            <StatItem>
              <StatLabel>{t('profitShared')}</StatLabel>
              <StatValue>
                {formatDecimal(summary?.totalProfitShared ?? 0, 2)} USDT
              </StatValue>
            </StatItem>
          </StatsRow>
        </CompactHeader>

        {/* Alerts */}
        {alerts.length > 0 && (
          <AlertsSection>
            {alerts.map((alert, index) => (
              <AlertCard key={index} severity={alert.severity}>
                <AlertContent>
                  <strong>{alert.type}</strong>: {alert.message}
                </AlertContent>
                <Button
                  size="small"
                  onClick={() => removeAlert(alert.followId)}
                >
                  {t('close')}
                </Button>
              </AlertCard>
            ))}
          </AlertsSection>
        )}

        {/* My Follows Table */}
        <Section isDark={isDarkMode}>
          <SectionTitle>
            {t('followingPortfolios')} ({(myFollows || []).length}개)
          </SectionTitle>
          <Table
            columns={followColumns}
            dataSource={myFollows || []}
            rowKey={(record) => record?.follow?.id || Math.random()}
            loading={isLoading}
            pagination={false}
            locale={{
              emptyText: isLoading ? '로딩 중...' : t('noFollowData'),
            }}
            scroll={{ x: 'max-content' }}
          />
        </Section>

        {/* Active Positions Table - 숨김 처리 */}
        {/* <Section isDark={isDarkMode}>
        <SectionTitle>
          {t('activePositions')}
          <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 8 }}>
            ({t('realtimeUpdate')})
          </span>
        </SectionTitle>
        <Table
          columns={positionColumns}
          dataSource={positions}
          rowKey={(record) => record.id}
          pagination={false}
        />
      </Section> */}
      </PageWrapper>
    </>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 1240px) {
    padding: 20px;
  }
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const ConnectionIndicator = styled.div<{ isConnected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ isConnected }) =>
    isConnected ? '#22c55e' : '#ef4444'};
  box-shadow: 0 0 0 2px
    ${({ isConnected }) =>
      isConnected ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  animation: ${({ isConnected }) =>
    isConnected ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr auto 1fr;
  align-items: center;
  gap: 20px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 110px;

  @media (max-width: 768px) {
    align-items: center;
    min-width: unset;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

const StatSubtext = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  margin-top: 4px;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const AlertsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  box-sizing: border-box;
`;

const AlertCard = styled.div<{ severity: 'info' | 'warning' | 'error' }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: ${({ severity }) =>
    severity === 'error'
      ? '#fef2f2'
      : severity === 'warning'
        ? '#fef3c7'
        : '#dbeafe'};
  color: ${({ severity }) =>
    severity === 'error'
      ? '#991b1b'
      : severity === 'warning'
        ? '#92400e'
        : '#1e40af'};
  border-left: 4px solid
    ${({ severity }) =>
      severity === 'error'
        ? '#ef4444'
        : severity === 'warning'
          ? '#f59e0b'
          : '#3b82f6'};
`;

const AlertContent = styled.div`
  flex: 1;
`;

const Section = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 20px;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.text};
`;

const ActionsGroup = styled.div`
  display: flex;
  gap: 8px;
`;
